import React, { useState } from 'react';
import * as S from './DeleteLog.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import { Log, useLogs } from '../hooks';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { restoreLog, selectLogsChecked, setLogsChecked } from '../redux';
import Loader from 'pages/components/Loader/Loader';
import { toast } from 'utils/toast';
import { selectCurrency } from 'pages/redux';
import { useSocketEmit } from 'pages/Navigation/hooks';
import Restore from '@mui/icons-material/Restore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const RestoreLog = ({
  onExit,
  log,
}: {
  onExit: Function,
  log: Log,
}) => {
  const displayLog = { ...log };
  const snapshot = displayLog.logTable;
  const logsChecked = useAppSelector(selectLogsChecked);
  const currency = useAppSelector(selectCurrency);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { updateLocalLog } = useLogs();
  const socketEmit = useSocketEmit();
  const dispatch = useAppDispatch();

  const onRestoreLog = async () => {
    if (!snapshot?.id) {
      toast('This deleted entry cannot be restored', { type: 'error' });
      return;
    }

    setIsLoading(true);
    const res = await dispatch(restoreLog({ auditId: displayLog.id.toString() }));

    if (res.status === 'success') {
      const { restored, audit } = res.data || {};
      if (restored) {
        updateLocalLog(restored as Log);
        // Re-inserted row — treat as add for other clients.
        socketEmit('update_log', { log: restored });
      }
      if (audit) {
        updateLocalLog(audit as Log);
        socketEmit('update_log', { log: audit });
      } else {
        updateLocalLog({ ...displayLog, logTable: undefined });
      }

      let newChecked = [...logsChecked.filter((c: string) => c !== 'Audit restored'), 'Audit restored'];
      if (snapshot.type && !newChecked.includes(snapshot.type)) {
        newChecked = [...newChecked, snapshot.type];
      }
      dispatch(setLogsChecked(newChecked));

      setIsLoading(false);
      toast('Successfully restored audit', { type: 'success' });
      onExit(true);
      return;
    }

    setIsLoading(false);
    toast(res.data, { type: 'error' });
  };

  const amount = snapshot?.value;
  const amountLabel = amount != null && !Number.isNaN(Number(amount))
    ? `${currency}${Number(amount).toLocaleString()}`
    : (amount || 'N/A');
  const typeLabel = snapshot?.type || 'audit';

  return (
    <>
      {
        isLoading ? <Loader isSticky /> : (
          <S.Container>
            <EscapeButton onClick={() => onExit()} />
            <S.Header>
              <S.SuccessIcon aria-hidden>
                <Restore fontSize="inherit" />
              </S.SuccessIcon>
              <S.MainLabel>Restore audit entry</S.MainLabel>
              <S.SubLabel>
                This brings the deleted entry back into your audit log.
              </S.SubLabel>
            </S.Header>
            <S.EntryCard>
              <S.EntryCardLabel>Amount</S.EntryCardLabel>
              <S.EntryAmount>{amountLabel}</S.EntryAmount>
              <S.EntryMeta>
                {typeLabel}
                {' · '}
                <S.EntryId>audit</S.EntryId>
              </S.EntryMeta>
            </S.EntryCard>
            <S.Info>
              <InfoOutlined fontSize="inherit" />
              <span>
                {'Are you sure you want to restore this '}
                <strong>{typeLabel}</strong>
                {' entry?'}
              </span>
            </S.Info>
            <S.Actions>
              <S.SecondaryBtn type="button" onClick={() => onExit()}>
                Cancel
              </S.SecondaryBtn>
              <S.RestoreBtn type="button" onClick={() => onRestoreLog()}>
                Restore entry
              </S.RestoreBtn>
            </S.Actions>
          </S.Container>
        )
      }
    </>
  );
};

export default RestoreLog;
